import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { esignatureService } from "../../services/esignature";
import "./DocumentList.css";
import DocumentViewerModal from "../Recruiting/DocumentViewerModal";
import { handleGenericError } from "../../utils/errorHandler";

// Helper function to extract recipient display name
const getRecipientDisplayName = recipient => {
  if (!recipient) return "";

  // Check for name field
  if (recipient.name) return recipient.name;

  // Check for first_name/last_name
  if (recipient.first_name || recipient.last_name) {
    return `${recipient.first_name || ""} ${recipient.last_name || ""}`.trim();
  }

  // Check for nested lead object
  if (recipient.lead) {
    if (recipient.lead.first_name || recipient.lead.last_name) {
      return `${recipient.lead.first_name || ""} ${recipient.lead.last_name || ""}`.trim();
    }
    if (recipient.lead.name) return recipient.lead.name;
  }

  // Check for nested user object
  if (recipient.user) {
    if (recipient.user.first_name || recipient.user.last_name) {
      return `${recipient.user.first_name || ""} ${recipient.user.last_name || ""}`.trim();
    }
    if (recipient.user.name) return recipient.user.name;
  }

  // Fallback to email
  return recipient.email || "Unknown";
};

const DocumentList = ({
  showTemplatesButton = true,
  breadcrumbRootLabel = "Admin",
  breadcrumbRootPath = "/admin",
}) => {
  const navigate = useNavigate();
  const { getVerifiedJWT } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [showViewerModal, setShowViewerModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const jwt = await getVerifiedJWT();
      const status = filterStatus === "all" ? null : filterStatus;
      const result = await esignatureService.getDocuments(jwt, status);

      // Ensure we always set an array
      let documentsArray = [];
      if (Array.isArray(result)) {
        documentsArray = result;
      } else if (result && Array.isArray(result.data)) {
        documentsArray = result.data;
      } else if (result && Array.isArray(result.documents)) {
        documentsArray = result.documents;
      } else if (
        result &&
        result.data &&
        Array.isArray(result.data.documents)
      ) {
        documentsArray = result.data.documents;
      } else if (result && typeof result === "object") {
        // Backend might be returning an object with documents as keys

        // Try to extract documents from object values if it's an object
        const values = Object.values(result.data || result);
        if (values.length > 0 && values[0] && typeof values[0] === "object") {
          documentsArray = values;
        } else {
          documentsArray = [];
        }
      }

      setDocuments(documentsArray);
    } catch (error) {
      handleGenericError(error, "Error loading documents");
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = status => {
    const statusMap = {
      draft: "status-draft",
      sent: "status-sent",
      in_progress: "status-inprogress",
      completed: "status-completed",
      voided: "status-voided",
      expired: "status-expired",
    };
    return statusMap[status] || "status-draft";
  };

  const formatDate = dateString => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // eslint-disable-next-line no-unused-vars
  const handleUploadDocument = () => {
    setShowUploadModal(true);
  };

  // eslint-disable-next-line no-unused-vars
  const handleDownload = async (e, doc) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      const jwt = await getVerifiedJWT();
      const result = await esignatureService.downloadSignedDocument(
        doc.id,
        jwt
      );
      // Create a temporary link and trigger download
      const link = document.createElement("a");
      link.href = result.path;
      link.download = result.filename;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      alert("Failed to download signed document");
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleDeleteClick = (e, doc) => {
    e.stopPropagation();
    e.preventDefault();
    setDocumentToDelete(doc);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!documentToDelete) return;

    try {
      const jwt = await getVerifiedJWT();
      await esignatureService.deleteDocument(documentToDelete.id, jwt);
      setShowDeleteModal(false);
      setDocumentToDelete(null);
      loadDocuments();
    } catch (error) {
      alert("Failed to delete document");
      setShowDeleteModal(false);
      setDocumentToDelete(null);
    }
  };

  const handleDocumentClick = doc => {
    setSelectedDocument(doc);
    setShowViewerModal(true);
  };

  return (
    <div className="document-list-container">
      <div className="breadcrumb">
        <span
          className="breadcrumb-item"
          onClick={() => navigate(breadcrumbRootPath)}
        >
          {breadcrumbRootLabel}
        </span>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-item breadcrumb-current">
          E-Signature Documents
        </span>
      </div>
      <div className="list-header">
        <div>
          <h1>E-Signature Documents</h1>
          <p className="subtitle">Manage and send documents for signature</p>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          {showTemplatesButton && (
            <button
              className="btn-secondary"
              onClick={() => navigate("/esignature/templates")}
            >
              Templates
            </button>
          )}
          {/* <button className="btn-upload" onClick={handleUploadDocument}>
            + Upload Document
          </button> */}
        </div>
      </div>

      <div className="filters">
        <button
          className={filterStatus === "all" ? "active" : ""}
          onClick={() => setFilterStatus("all")}
        >
          All
        </button>
        <button
          className={filterStatus === "draft" ? "active" : ""}
          onClick={() => setFilterStatus("draft")}
        >
          Drafts
        </button>
        <button
          className={filterStatus === "sent" ? "active" : ""}
          onClick={() => setFilterStatus("sent")}
        >
          Sent
        </button>
        <button
          className={filterStatus === "completed" ? "active" : ""}
          onClick={() => setFilterStatus("completed")}
        >
          Completed
        </button>
      </div>

      {loading ? (
        <div className="loading-state">Loading documents...</div>
      ) : documents.length === 0 ? (
        <div className="empty-state">
          <h3>No documents found</h3>
          <p>Upload a document to get started</p>
          {/* <button className="btn-upload-large" onClick={handleUploadDocument}>
            Upload Your First Document
          </button> */}
        </div>
      ) : (
        <div className="documents-list-view">
          <div className="documents-list-header">
            <div className="list-col col-title">Document Title</div>
            <div className="list-col col-direct-seller">Direct Seller</div>
            <div className="list-col col-guarantor">Guarantor</div>
            <div className="list-col col-status">Status</div>
            <div className="list-col col-sent">Date Sent</div>
          </div>
          <div className="documents-list-body">
            {Array.isArray(documents) &&
              documents.map(doc => {
                // Find specific recipient types - try multiple approaches
                let directSeller = doc.recipients?.find(
                  r =>
                    r.recipient_type === "direct_seller" ||
                    r.name?.toLowerCase().includes("seller") ||
                    r.email?.toLowerCase().includes("seller")
                );

                let guarantor = doc.recipients?.find(
                  r =>
                    r.recipient_type === "guarantor" ||
                    r.name?.toLowerCase().includes("guarantor") ||
                    r.email?.toLowerCase().includes("guarantor")
                );

                // Fallback: if no specific types found, use first two recipients
                if (
                  !directSeller &&
                  !guarantor &&
                  doc.recipients &&
                  doc.recipients.length > 0
                ) {
                  const allRecipients = doc.recipients.filter(
                    r => r.role === "signer" || !r.role
                  );
                  if (allRecipients.length > 0) {
                    directSeller = allRecipients[0];
                    if (allRecipients.length > 1) {
                      guarantor = allRecipients[1];
                    }
                  }
                }

                return (
                  <div
                    key={doc.id}
                    className="document-list-row"
                    onClick={() => handleDocumentClick(doc)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="list-col col-title">
                      <div className="doc-name-wrapper">
                        <span className="material-symbols-outlined doc-icon">
                          description
                        </span>
                        <div className="doc-name-info">
                          <div className="doc-title">{doc.title}</div>
                          {doc.description && (
                            <div className="doc-description">
                              {doc.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="list-col col-direct-seller">
                      {directSeller ? (
                        <div className="recipient-cell">
                          <span className="recipient-name">
                            {getRecipientDisplayName(directSeller)}
                          </span>
                          {directSeller.has_signed && (
                            <span
                              className="material-symbols-outlined signed-icon"
                              title="Signed"
                            >
                              check_circle
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="no-recipient">—</span>
                      )}
                    </div>
                    <div className="list-col col-guarantor">
                      {guarantor ? (
                        <div className="recipient-cell">
                          <span className="recipient-name">
                            {getRecipientDisplayName(guarantor)}
                          </span>
                          {guarantor.has_signed && (
                            <span
                              className="material-symbols-outlined signed-icon"
                              title="Signed"
                            >
                              check_circle
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="no-recipient">—</span>
                      )}
                    </div>
                    <div className="list-col col-status">
                      <span
                        className={`status-badge ${getStatusBadgeClass(doc.status)}`}
                      >
                        {doc.status === "in_progress"
                          ? "In Progress"
                          : doc.status}
                      </span>
                    </div>
                    <div className="list-col col-sent">
                      {doc.sent_at ? formatDate(doc.sent_at) : "—"}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            setShowUploadModal(false);
            loadDocuments();
          }}
        />
      )}

      {showDeleteModal && documentToDelete && (
        <DeleteConfirmModal
          documentTitle={documentToDelete.title}
          onClose={() => {
            setShowDeleteModal(false);
            setDocumentToDelete(null);
          }}
          onConfirm={handleConfirmDelete}
        />
      )}

      {showViewerModal && selectedDocument && (
        <DocumentViewerModal
          isOpen={showViewerModal}
          onClose={() => {
            setShowViewerModal(false);
            setSelectedDocument(null);
          }}
          document={selectedDocument}
        />
      )}
    </div>
  );
};

const UploadModal = ({ onClose, onSuccess }) => {
  const { getVerifiedJWT } = useAuth();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!file || !title) {
      alert("Please provide a title and select a file");
      return;
    }

    try {
      setUploading(true);
      const jwt = await getVerifiedJWT();
      const formData = new FormData();
      formData.append("file", file);
      formData.append("title", title);
      if (description) formData.append("description", description);

      const result = await esignatureService.createDocument(formData, jwt);
      const documentId = result?.data?.id || result?.id;

      if (!documentId) {
        throw new Error("Failed to get document ID from response");
      }

      // Document created successfully
      onSuccess();
    } catch (error) {
      alert("Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <h2>Upload Document</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Document Title *</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Enter document title"
              required
            />
          </div>
          <div className="form-group">
            <label>Description (optional)</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Enter document description"
              rows="3"
            />
          </div>
          <div className="form-group">
            <label>File (PDF) *</label>
            <input
              type="file"
              accept=".pdf"
              onChange={e => setFile(e.target.files[0])}
              required
            />
            {file && (
              <div className="file-info">
                Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
              </div>
            )}
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={uploading || !file || !title}
            >
              {uploading ? "Uploading..." : "Upload"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DeleteConfirmModal = ({ documentTitle, onClose, onConfirm }) => {
  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        className="modal-content"
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "32px",
          maxWidth: "450px",
          width: "90%",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
        }}
      >
        <h2
          style={{
            margin: "0 0 16px 0",
            fontSize: "24px",
            fontWeight: 600,
            color: "#e74c3c",
          }}
        >
          ⚠️ Delete Draft
        </h2>
        <p
          style={{
            margin: "0 0 24px 0",
            fontSize: "15px",
            lineHeight: "1.6",
            color: "#555",
          }}
        >
          Are you sure you want to delete <strong>"{documentTitle}"</strong>?
          <br />
          <br />
          This action cannot be undone.
        </p>
        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "10px 24px",
              fontSize: "15px",
              fontWeight: 500,
              border: "2px solid #ddd",
              borderRadius: "6px",
              backgroundColor: "white",
              color: "#666",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = "#999";
              e.currentTarget.style.color = "#333";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = "#ddd";
              e.currentTarget.style.color = "#666";
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: "10px 24px",
              fontSize: "15px",
              fontWeight: 600,
              border: "none",
              borderRadius: "6px",
              backgroundColor: "#e74c3c",
              color: "white",
              cursor: "pointer",
              transition: "all 0.2s",
              boxShadow: "0 4px 12px rgba(231, 76, 60, 0.3)",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = "#c0392b";
              e.currentTarget.style.boxShadow =
                "0 6px 16px rgba(231, 76, 60, 0.4)";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = "#e74c3c";
              e.currentTarget.style.boxShadow =
                "0 4px 12px rgba(231, 76, 60, 0.3)";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentList;
